import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Collapse,
} from '@mui/material';
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // 에러 로깅 (실제 프로덕션에서는 외부 서비스로 전송)
    console.error('Error caught by boundary:', error, errorInfo);
    
    // 선택적으로 에러 리포팅 서비스에 전송
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { title = '문제가 발생했습니다', compact = false } = this.props;

      if (compact) {
        return (
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={this.handleReset}>
                다시 시도
              </Button>
            }
          >
            {title}
          </Alert>
        );
      }

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={2}
        >
          <Card sx={{ maxWidth: 600, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <ErrorIcon 
                sx={{ 
                  fontSize: 80, 
                  color: 'error.main',
                  mb: 2, 
                }} 
              />
              
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" paragraph>
                예상치 못한 오류가 발생했습니다. 잠시 후 다시 시도하거나 페이지를 새로고침해 주세요.
              </Typography>

              <Box display="flex" gap={2} justifyContent="center" mb={2}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReset}
                  color="primary"
                >
                  다시 시도
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={this.handleReload}
                  color="primary"
                >
                  페이지 새로고침
                </Button>
              </Box>

              {process.env.NODE_ENV === 'development' && (
                <>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<BugReportIcon />}
                    onClick={this.toggleDetails}
                    sx={{ mt: 1 }}
                  >
                    {this.state.showDetails ? '오류 세부정보 숨기기' : '오류 세부정보 보기'}
                  </Button>

                  <Collapse in={this.state.showDetails}>
                    <Box 
                      sx={{ 
                        mt: 2, 
                        p: 2, 
                        bgcolor: 'grey.100', 
                        borderRadius: 1,
                        textAlign: 'left',
                      }}
                    >
                      <Typography variant="caption" component="div" gutterBottom>
                        <strong>Error:</strong>
                      </Typography>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          fontSize: '0.75rem',
                          wordBreak: 'break-all',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {this.state.error && this.state.error.toString()}
                      </Typography>

                      {this.state.errorInfo && (
                        <>
                          <Typography variant="caption" component="div" gutterBottom sx={{ mt: 2 }}>
                            <strong>Component Stack:</strong>
                          </Typography>
                          <Typography 
                            variant="body2" 
                            component="pre" 
                            sx={{ 
                              fontSize: '0.75rem',
                              wordBreak: 'break-all',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {this.state.errorInfo.componentStack}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;